import cn from 'classnames';
import { splitTitle, mapTime } from 'components/Shared/Product/selector';
import { ProductTag, getProductTagKey } from './productTag';
import { StarRating } from 'components/Shared/StarRating';
import Image from 'components/Shared/Image';
import styles from './styles.scss';
import { NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { BhImage } from 'models/BhImage';
import { NotifyLink } from 'components/Shared/NotifyLink';
import { SrcSet } from 'components/Shared/Image/SrcSet';
import ResponsiveImage from 'components/Shared/Image/ResponsiveImage';
import { IBasicProduct } from 'models/BasicProduct';
import { isEmpty } from 'utils/isEmpty';
import { trimEnd } from 'utils/trim';
import { Time } from 'models/ProductMeta';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import { useState, memo, forwardRef, Ref, useEffect } from 'react';
import { useDebounce } from 'hooks';
import connect from 'components/Shared/connect';
import { isMobile } from 'components/Shared/responsive';
import { FavouriteToggle } from 'components/FavouriteToggle';
import { EnergyData } from 'components/EnergyData';
import {
  CardPrice,
  PriceProps as BetterPriceProps
} from 'components/Shared/Price/themes/BGH/cardPrice';
import { useSelector } from 'react-redux';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { Hero } from '../../hero';
import { TacticalFlag, TacticalFlagProps } from 'components/TacticalFlag';
import { GtmProduct } from 'store/googleTagManager/types';
import { createGtmProduct } from 'store/googleTagManager/events/utils';
import { RecommendedPrice } from 'views/ProductPage/prices/recommendedPrice';

interface ImageProps {
  isListView?: boolean;
  isLazy?: boolean;
  productId: string;
  ticket: string;
  url: string;
  image?: BhImage;
  picture: {
    className?: string;
    height: number;
    width: number;
  };
  className?: string;
  isSponsored?: boolean;
  customSizes?: { [key: string]: SrcSet };
  isDisabled?: boolean;
}

interface TitleClassNames {
  outerWrapper?: string;
  innerWrapper?: string;
}

type TitleProps = Pick<IBasicProduct, 'titleSplitPos' | 'title' | 'ticket' | 'url'> & {
  classNames?: TitleClassNames;
  isCategoryBold?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
};

const DEFAULT_WIDTH = 321;
const MEDIUM_WIDTH = 229;

const sizes: { [key: string]: SrcSet } = {
  default: {
    viewWidth: DEFAULT_WIDTH,
    width: DEFAULT_WIDTH,
    height: DEFAULT_WIDTH
  },
  medium: {
    viewWidth: MEDIUM_WIDTH,
    width: MEDIUM_WIDTH,
    height: MEDIUM_WIDTH
  }
};

interface SponsoredProps {
  isListView?: boolean;
}

const Sponsored = ({ isListView }: SponsoredProps) => {
  return (
    <div
      className={cn(styles.switchSponsoredContainer, {
        [styles.desktopSponsoredContainer]: !isListView
      })}
    >
      <p className={styles.switchSponsoredText}>Sponsrad</p>
    </div>
  );
};

const HeaderImage = ({
  image,
  url,
  picture,
  ticket,
  className,
  isSponsored,
  isListView,
  isLazy,
  productId,
  customSizes,
  isDisabled,
  onLinkClick,
  gtmProduct
}: ImageProps & Pick<OwnProps, 'onLinkClick' | 'gtmProduct'>) => {
  const s = customSizes || sizes;
  const responsive: ResponsiveImage = {
    srcSet: Object.values(s),
    sizes: `(max-width: 768px) ${s.default.width}px,
    ${s.medium.width}px`
  };

  return (
    <div className={className}>
      <NotifyLink
        url={url}
        ticket={ticket}
        className={className}
        isDisabled={isDisabled}
        onClick={onLinkClick}
      >
        <Image
          lazy={isLazy}
          wrapperStyle={{ width: '100%' }}
          className={cn(styles.image, picture.className, {
            [styles.imageWithoutBorder]: isListView
          })}
          paddingBottom
          {...image}
          responsive={responsive}
          height={picture.height}
          width={picture.width}
          alt={image?.alt}
        />
        {isSponsored && <Sponsored isListView={isListView} />}
      </NotifyLink>
      <FavouriteToggle className={styles.favourite} productId={productId} gtmProduct={gtmProduct} />
    </div>
  );
};

export const Title = (props: TitleProps) => {
  const { classNames = {}, url, isCategoryBold, ticket, isDisabled, onClick } = props;
  const { title, category } = splitTitle(props);
  return (
    <NotifyLink
      className={classNames.outerWrapper}
      url={url}
      ticket={ticket}
      isDisabled={isDisabled}
      onClick={onClick}
    >
      <p className={cn(classNames.innerWrapper, styles.title)}>
        <span className={cn({ [styles.bold]: isCategoryBold })}>{category} </span>
        <span className={cn({ [styles.bold]: !isCategoryBold })}>{title} </span>
      </p>
    </NotifyLink>
  );
};

interface ReviewProps extends TacticalFlagProps {
  score: number;
  isTopTested?: boolean;
  isNewProduct: boolean;
}

const Review = ({
  score,
  isTopTested,
  isNewProduct,
  tacticalFlagMarking,
  tacticalFlagColor
}: ReviewProps) => {
  const roundedScore = Math.floor(score);
  const _score = <StarRating score={score} />;
  const tagType = getProductTagKey({ isNewProduct, isTopTested });
  const _tag = <ProductTag type={tagType} />;
  const _flag = (
    <TacticalFlag tacticalFlagMarking={tacticalFlagMarking} tacticalFlagColor={tacticalFlagColor} />
  );
  const shouldRender = !!roundedScore || !!tagType || !!tacticalFlagMarking;

  return shouldRender ? (
    <div className={styles.reviewContainer}>
      {_score}
      {_tag}
      {_flag}
    </div>
  ) : (
    NO_ELEMENT
  );
};

interface DeliveryProps {
  isListView?: boolean;
  meta?: {
    deliveryEstimate?: number;
    showDeliveryInfo: boolean;
    stock: string;
  };
}

const showStock = (showDeliveryInfo: boolean, stock: string): boolean => {
  return !showDeliveryInfo && !isEmpty(stock);
};

const isShortDeliveryTime = (deliveryEstimate: number): boolean => {
  switch (deliveryEstimate) {
    case Time.TwoToThreeWorkDays:
    case Time.TwoToFiveWorkDays:
    case Time.ThreeToFiveWorkDays:
    case Time.FourToSevenWorkDays:
    case Time.FiveToTenWorkDays:
      return true;
    default:
      return false;
  }
};

const Delivery = ({ meta, isListView }: DeliveryProps) => {
  if (
    !meta ||
    !meta.deliveryEstimate ||
    isListView ||
    (isShortDeliveryTime(meta.deliveryEstimate) && !showStock(meta.showDeliveryInfo, meta.stock))
  ) {
    return NO_ELEMENT;
  }

  const delivery = getDelivery({ meta });
  return <p className={styles.deliveryText}>{delivery}</p>;
};

const getDelivery = ({ meta }: Pick<DeliveryProps, 'meta'>): string => {
  if (!meta?.deliveryEstimate) {
    return '';
  }

  return showStock(meta.showDeliveryInfo, meta.stock)
    ? trimEnd(meta.stock, ':')
    : `${rm('filter_title_p_deltime')} ${mapTime(meta.deliveryEstimate)}`;
};

interface VariantOptionProps extends Pick<ProductlistViewProduct, 'colorOptions'> {
  options?: string[];
  isListView?: boolean;
  setImage?: (image: BhImage) => void;
  defaultImage: BhImage;
  isMobile: boolean;
  hideVariants?: boolean;
  maxVariants?: number;
}

interface VariantImages {
  colors: BhImage[];
}

const getHasDuplicateVariantImages = ({ colors }: VariantImages) => {
  const MINIMUM_AMOUNT = 1;
  const ids = new Set(colors.map(color => color.id));
  return ids.size <= MINIMUM_AMOUNT;
};

const _VariantOptions = memo(
  ({
    colorOptions,
    url,
    ticket,
    setImage,
    defaultImage,
    hideVariants,
    maxVariants = 0,
    isListView,
    isMobile,
    onLinkClick
  }: VariantOptionProps & ImageProps & Pick<OwnProps, 'onLinkClick'>) => {
    const VARIANT_DEBOUNCE_TIME = 150;
    const IMAGE_SIZE = 64;
    const [debounce] = useDebounce(
      (updateImage: () => void) => updateImage(),
      VARIANT_DEBOUNCE_TIME
    );
    if (!colorOptions?.nbrOfColors || hideVariants) {
      return NO_ELEMENT;
    }
    const { colors, nbrOfColors } = colorOptions;
    const hasDuplicateVariantImages = getHasDuplicateVariantImages({ colors });
    const maxWithoutDuplicates = hasDuplicateVariantImages ? 1 : maxVariants;
    const hasTooManyOptions = nbrOfColors > maxWithoutDuplicates;
    const amountOfTooManyOptions = hasTooManyOptions ? nbrOfColors - maxWithoutDuplicates : 0;
    const visibleOptions = colors.slice(0, maxWithoutDuplicates);

    return (
      <div className={cn(styles.optionContainer, { [styles.removeMargin]: isListView })}>
        {visibleOptions.map((option, index) => {
          const updateImage = () => {
            !isMobile && debounce(() => setImage && setImage(option));
          };

          const setDefaultImage = () => {
            !isMobile && debounce(() => setImage && setImage(defaultImage));
          };

          return (
            <NotifyLink
              key={`${index}-${option.id}`}
              url={url}
              ticket={ticket}
              className={styles.option}
              onClick={onLinkClick}
            >
              <Image
                onMouseOver={updateImage}
                onMouseOut={setDefaultImage}
                wrapperStyle={{ width: '100%' }}
                className={styles.image}
                paddingBottom
                {...option}
                height={IMAGE_SIZE}
                width={IMAGE_SIZE}
                alt={option.alt}
              />
            </NotifyLink>
          );
        })}
        {!!amountOfTooManyOptions && (
          <NotifyLink
            url={url}
            ticket={ticket}
            className={styles.tooManyOptions}
            onClick={onLinkClick}
          >
            <p className={styles.tooManyOptionsText}>+{amountOfTooManyOptions}</p>
          </NotifyLink>
        )}
      </div>
    );
  }
);

const VariantOptions = connect(state => ({
  isMobile: isMobile(state)
}))(_VariantOptions);

interface OwnProps extends ProductlistViewProduct {
  isListView?: boolean;
  isTopTested: boolean;
  isSponsored?: boolean;
  isCategoryBold?: boolean;
  setImage?: (image: BhImage) => void;
  image?: BhImage;
  isSponsoredByListView?: boolean;
  hideVariants?: boolean;
  colours: string[];
  page?: string;
  maxVariants?: number;
  picture: {
    className?: string;
    height: number;
    width: number;
  };
  inList?: boolean;
  children?: React.ReactNode;
  customSizes: { [key: string]: SrcSet };
  isLazy?: boolean;
  isDisabled?: boolean;
  isPurchable?: boolean;
  heroDisplay?: boolean;
  onLinkClick?: () => void;
  gtmProduct?: GtmProduct;
}

const ProductCardImages = ({
  isListView,
  productImage,
  url,
  picture,
  ticket,
  isSponsored,
  setImage,
  image,
  colorOptions,
  hideVariants,
  maxVariants,
  customSizes,
  productId,
  isLazy,
  isDisabled,
  heroDisplay,
  onLinkClick,
  gtmProduct
}: Pick<
  OwnProps,
  | 'isListView'
  | 'productImage'
  | 'url'
  | 'picture'
  | 'ticket'
  | 'isSponsored'
  | 'colorOptions'
  | 'hideVariants'
  | 'maxVariants'
  | 'customSizes'
  | 'setImage'
  | 'image'
  | 'productId'
  | 'isLazy'
  | 'isDisabled'
  | 'heroDisplay'
  | 'onLinkClick'
  | 'gtmProduct'
>) => {
  return (
    <>
      <HeaderImage
        isLazy={isLazy}
        productId={productId}
        image={image}
        url={url}
        picture={picture}
        ticket={ticket}
        className={cn({
          [styles.switchHeaderImage]: isListView,
          [styles.headerStyle]: !isListView,
          [styles.heroDisplay]: heroDisplay
        })}
        isSponsored={isSponsored}
        isListView={isListView}
        customSizes={customSizes}
        isDisabled={isDisabled}
        onLinkClick={onLinkClick}
        gtmProduct={gtmProduct}
      />
      <VariantOptions
        isListView={isListView}
        maxVariants={maxVariants}
        setImage={setImage}
        hideVariants={hideVariants || isListView}
        defaultImage={productImage}
        colorOptions={colorOptions}
        image={productImage}
        url={url}
        picture={picture}
        ticket={ticket}
        onLinkClick={onLinkClick}
      />
    </>
  );
};

const thecaRequest = (thecaUrl?: string) => {
  if (thecaUrl) {
    window.navigator.sendBeacon(
      `/api/notification/thecaNotify?thecaUrl=${encodeURIComponent(thecaUrl)}`
    );
  }
};

export const ProductCard = forwardRef(
  (
    {
      productImage,
      url,
      title,
      titleSplitPos,
      price,
      isCampaign,
      isPartialCampaign,
      isLowPrice,
      isBundleSavings,
      isLoweredPrice,
      splashTextEnd,
      splashTextTop,
      isTopTested,
      productId,
      efficiencyDeclarationUrl,
      efficiencyArrow,
      reviewAvgScore,
      isNewProduct,
      meta,
      isCategoryBold,
      isListView,
      isSponsored,
      isSponsoredByListView,
      picture,
      ticket,
      colorOptions,
      hideVariants,
      isPartPrice,
      children,
      page,
      partPriceUnit,
      maxVariants,
      customSizes,
      isLazy,
      isDisabled,
      heroDisplay,
      usps,
      thecaUrl,
      tacticalFlagMarking,
      tacticalFlagColor,
      isFamily,
      onLinkClick,
      mainWebCatalog,
      brandName
    }: OwnProps & Pick<BetterPriceProps, 'page'>,
    ref?: Ref<HTMLDivElement>
  ) => {
    const [image, setImage] = useState(productImage);
    const isMobile = useSelector(isMobileSelector);
    useEffect(() => {
      if (image.id !== productImage.id) {
        setImage(productImage);
      }
    }, [productImage, image]);

    if (!productId) {
      return NO_ELEMENT;
    }

    const gtmProduct: GtmProduct = createGtmProduct({
      url,
      brandName,
      productId,
      title,
      variantName: '',
      presentationPrice: price.presentationPrice,
      item_web_catalog_id: mainWebCatalog
    });

    const hasOptions = colorOptions?.nbrOfColors > 1;
    const energyData = (
      <EnergyData
        productId={productId}
        efficiencyArrow={efficiencyArrow}
        efficiencyDeclarationUrl={efficiencyDeclarationUrl}
        marginBottom={page !== 'productpage' ? true : false}
      />
    );

    const review = (
      <Review
        score={reviewAvgScore}
        isTopTested={isTopTested}
        isNewProduct={isNewProduct}
        tacticalFlagMarking={tacticalFlagMarking}
        tacticalFlagColor={tacticalFlagColor}
      />
    );
    const delivery = <Delivery meta={meta} isListView={isListView} />;

    const ProductInformationCard = (
      <div>
        <NotifyLink url={url} ticket={ticket} isDisabled={!isMobile} style={{ height: '100%' }}>
          <Title
            {...{ isCategoryBold, title, titleSplitPos, ticket, url, isDisabled: isMobile }}
            onClick={onLinkClick}
          />
          <RecommendedPrice
            priceString={price.recommendedPriceString}
            isPartialCampaign={isPartialCampaign}
          />
          <CardPrice
            page={page ? page : isListView ? 'switchProductlist' : 'productlist'}
            splashTextEnd={splashTextEnd}
            splashTextTop={splashTextTop}
            isCampaign={isCampaign}
            isPartialCampaign={isPartialCampaign}
            isLowPrice={isLowPrice}
            isLoweredPrice={isLoweredPrice}
            isBundleSavings={isBundleSavings}
            price={price}
            salesUnit={partPriceUnit}
            inCard
            partPrice={isPartPrice ? price.presentationPrice : 0}
            partPriceUnit={partPriceUnit}
            isFamily={isFamily}
          />
          {!isMobile && energyData}
          {review}
          {delivery}
        </NotifyLink>
        <VariantOptions
          maxVariants={maxVariants}
          setImage={setImage}
          hideVariants={hideVariants || !isListView}
          defaultImage={productImage}
          colorOptions={colorOptions}
          image={productImage}
          url={url}
          picture={picture}
          ticket={ticket}
          productId={productId}
          isListView={isListView}
          onLinkClick={onLinkClick}
        />
        {isMobile && energyData}
      </div>
    );

    const images = (
      <ProductCardImages
        maxVariants={maxVariants}
        hideVariants={hideVariants}
        colorOptions={colorOptions}
        picture={picture}
        productImage={productImage}
        ticket={ticket}
        url={url}
        isListView={isListView}
        isSponsored={isSponsored}
        customSizes={customSizes}
        setImage={setImage}
        image={image}
        productId={productId}
        isLazy={isLazy}
        isDisabled={isDisabled}
        heroDisplay={heroDisplay}
        onLinkClick={onLinkClick}
        gtmProduct={gtmProduct}
      />
    );

    if (heroDisplay) {
      return (
        <Hero
          image={images}
          price={
            <CardPrice
              page={'productListHero'}
              splashTextEnd={splashTextEnd}
              splashTextTop={splashTextTop}
              isCampaign={isCampaign}
              isLowPrice={isLowPrice}
              isLoweredPrice={isLoweredPrice}
              price={price}
              salesUnit={partPriceUnit}
              inCard
              partPrice={isPartPrice ? price.presentationPrice : 0}
              partPriceUnit={partPriceUnit}
            />
          }
          energyInformation={energyData}
          review={review}
          deliveryInformation={delivery}
          titleSplitPos={titleSplitPos}
          fullTitle={title}
          url={url}
          ticket={ticket}
          usps={usps}
        />
      );
    }

    return (
      <div
        onClick={() => thecaRequest(thecaUrl)}
        className={cn(styles.productCardContainer, {
          [styles.cellStyle]: isListView,
          [styles.sponsoredByListView]: isSponsoredByListView
        })}
        {...getQaDataAttr(`productcard-${productId}`)}
      >
        {images}
        <div
          ref={ref}
          className={cn({
            [styles.switchDetailsContainer]: isListView,
            [styles.optionSpacing]: hasOptions
          })}
        >
          {ProductInformationCard}
        </div>
        <div>{children}</div>
      </div>
    );
  }
);
